import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Buildings, UploadSimple, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", address: "", tax_office: "", tax_no: "", phone: "", email: "", website: "", iban: "", logo: "" };

export default function CompanyForm({ canEdit }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => { api.get("/company").then((r) => setF({ ...EMPTY, ...r.data })).catch(() => {}); }, []);

  const pickLogo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Lütfen bir görsel dosyası seçin");
    if (file.size > 400 * 1024) return toast.error("Logo en fazla 400 KB olmalı");
    const fr = new FileReader();
    fr.onload = () => setF((s) => ({ ...s, logo: String(fr.result) }));
    fr.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/company", { ...f, id: undefined });
      toast.success("Firma bilgileri kaydedildi");
    } catch (e2) {
      toast.error(errMsg(e2));
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="panel fade-up space-y-4 p-6" data-testid="company-form">
      <h3 className="flex items-center gap-2 font-display text-lg font-bold"><Buildings size={18} /> Firma Bilgileri</h3>
      <p className="text-[11px] text-muted-foreground">Bu bilgiler irsaliye ve fatura belgelerinin üst kısmında kullanılır.</p>
      <Field label="Firma Ünvanı"><Input value={f.name} onChange={set("name")} disabled={!canEdit} placeholder="Örn. Demir Yapı Malzemeleri Ltd. Şti." data-testid="company-name-input" /></Field>
      <Field label="Adres"><Input value={f.address} onChange={set("address")} disabled={!canEdit} data-testid="company-address-input" /></Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Vergi Dairesi"><Input value={f.tax_office} onChange={set("tax_office")} disabled={!canEdit} data-testid="company-tax-office-input" /></Field>
        <Field label="Vergi / TC No"><Input value={f.tax_no} onChange={set("tax_no")} disabled={!canEdit} data-testid="company-tax-no-input" /></Field>
        <Field label="Telefon"><Input value={f.phone} onChange={set("phone")} disabled={!canEdit} data-testid="company-phone-input" /></Field>
        <Field label="E-posta"><Input value={f.email} onChange={set("email")} disabled={!canEdit} data-testid="company-email-input" /></Field>
        <Field label="Web Sitesi"><Input value={f.website} onChange={set("website")} disabled={!canEdit} data-testid="company-website-input" /></Field>
        <Field label="IBAN"><Input value={f.iban} onChange={set("iban")} disabled={!canEdit} data-testid="company-iban-input" /></Field>
      </div>
      <div>
        <div className="label-eyebrow mb-2">Logo</div>
        <div className="flex items-center gap-3">
          <div className="flex h-16 w-28 items-center justify-center border border-dashed border-border bg-muted/40" data-testid="company-logo-preview">
            {f.logo ? <img src={f.logo} alt="Logo" className="max-h-14 max-w-24 object-contain" /> : <span className="text-[10px] text-muted-foreground">Logo yok</span>}
          </div>
          {canEdit && (
            <>
              <label className="inline-flex cursor-pointer items-center gap-2 border border-border px-3 py-2 text-sm hover:bg-muted" data-testid="company-logo-upload-label">
                <UploadSimple size={16} /> Logo Yükle
                <input type="file" accept="image/*" className="hidden" onChange={pickLogo} data-testid="company-logo-input" />
              </label>
              {f.logo && <Button type="button" variant="ghost" size="icon" className="text-red-600" onClick={() => setF((s) => ({ ...s, logo: "" }))} data-testid="company-logo-remove-button"><Trash /></Button>}
            </>
          )}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">PNG veya JPG, en fazla 400 KB.</p>
      </div>
      {canEdit && <Button type="submit" disabled={busy} className="rounded-[4px]" data-testid="company-save-button">{busy ? "Kaydediliyor…" : "Firma Bilgilerini Kaydet"}</Button>}
      {!canEdit && <p className="text-[11px] text-muted-foreground">Firma bilgilerini yalnızca yönetici düzenleyebilir.</p>}
    </form>
  );
}
